import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Building2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Obra {
  id: string;
  nombre: string;
  estado: string;
  horas_estimadas: number;
  horas_consumidas: number;
}

interface ObraSelectorFieldProps {
  clienteId: string;
  value: string;
  onChange: (value: string) => void;
}

export const ObraSelectorField: React.FC<ObraSelectorFieldProps> = ({
  clienteId,
  value,
  onChange,
}) => {
  const [obras, setObras] = useState<Obra[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadObras = async () => {
      if (!clienteId) {
        setObras([]);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('obras')
          .select('id, nombre, estado, horas_estimadas, horas_consumidas')
          .eq('cliente_id', clienteId)
          .eq('estado', 'activa')
          .order('nombre');

        if (error) throw error;
        setObras(data || []);
      } catch (error) {
        console.error('Error loading obras:', error);
      } finally {
        setLoading(false);
      }
    };

    loadObras();
  }, [clienteId]);

  if (!clienteId) return null;

  return (
    <div className="space-y-2">
      <Label htmlFor="obra" className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        Vincular a Obra (opcional)
      </Label>
      <Select value={value || 'none'} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Sin vincular a obra" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Sin vincular a obra</SelectItem>
          {obras.map((obra) => (
            <SelectItem key={obra.id} value={obra.id}>
              <div className="flex items-center gap-2">
                <span>{obra.nombre}</span>
                <Badge variant="outline" className="text-xs">
                  {obra.horas_consumidas}/{obra.horas_estimadas}h
                </Badge>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {obras.length === 0 && !loading && clienteId && (
        <p className="text-xs text-muted-foreground">
          Este cliente no tiene obras activas
        </p>
      )}
    </div>
  );
};
